import { useEffect, useRef, useState, useCallback } from 'react';

export interface WebSocketMessage {
  type: 'tool_start' | 'tool_output' | 'tool_complete' | 'tool_error';
  data: any;
  timestamp: string;
}

export interface ToolUpdate {
  tool: string;
  target: string;
  output?: string;
  type?: 'stdout' | 'stderr';
  command?: string;
  result?: any;
  error?: string;
}

// Global WebSocket instance to prevent multiple connections
let globalWs: WebSocket | null = null;
let connectionCount = 0;

export function useWebSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [toolUpdates, setToolUpdates] = useState<ToolUpdate[]>([]);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);

  const connectWebSocket = useCallback(() => {
    if (isConnectingRef.current || globalWs?.readyState === WebSocket.OPEN) {
      return;
    }

    isConnectingRef.current = true;
    
    try {
      // Close existing connection if any
      if (globalWs) {
        globalWs.close();
        globalWs = null;
      }

      const ws = new WebSocket('ws://localhost:3001');
      globalWs = ws;

      ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        isConnectingRef.current = false;
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setMessages(prev => {
            // Limit messages to prevent memory issues
            const newMessages = [...prev, message];
            if (newMessages.length > 1000) {
              return newMessages.slice(-500);
            }
            return newMessages;
          });
          
          // Process tool updates
          if (message.type === 'tool_output') {
            setToolUpdates(prev => {
              const newUpdates = [...prev, message.data];
              // Limit tool updates to prevent memory issues
              if (newUpdates.length > 500) {
                return newUpdates.slice(-250);
              }
              return newUpdates;
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        setConnectionError('WebSocket connection lost');
        isConnectingRef.current = false;
        globalWs = null;
        
        // Only attempt to reconnect if this is the last active connection
        if (connectionCount <= 1) {
          // Clear any existing reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          // Attempt to reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            if (connectionCount > 0) {
              connectWebSocket();
            }
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        setConnectionError('Failed to connect to WebSocket server');
        isConnectingRef.current = false;
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setIsConnected(false);
      setConnectionError('WebSocket not available');
      isConnectingRef.current = false;
    }
  }, []);

  useEffect(() => {
    connectionCount++;
    connectWebSocket();

    return () => {
      connectionCount--;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Only close the global connection if this is the last component using it
      if (connectionCount <= 0 && globalWs) {
        console.log('🔌 Closing WebSocket connection (last component unmounted)');
        globalWs.close();
        globalWs = null;
      }
    };
  }, [connectWebSocket]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setToolUpdates([]);
  }, []);

  const getToolOutput = useCallback((toolName: string) => {
    return toolUpdates
      .filter(update => update.tool === toolName)
      .map(update => update.output)
      .join('');
  }, [toolUpdates]);

  return {
    isConnected,
    messages,
    toolUpdates,
    connectionError,
    clearMessages,
    getToolOutput
  };
} 